import i18n from "@/i18n.ts";
import {
  ApiWorkflowEventTypes,
  ApiWorkflowTriggerScheduleEntityField,
  ApiWorkflowTriggerScheduleEntityType,
  ApiWorkflowTriggerType,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflow, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import {
  ApiEmployeeColumn,
  ApiEmployeeColumnValueType,
  EmployeeColumnEntity,
  getEmployeeColumnEntity,
} from "@/types/employees/employee-column.types.ts";
import { ConfirmationModal, SelectItem, useModals } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export enum WorkflowEditPageStep {
  details = "details",
  visibility = "visibility",
  automation = "automation",
  automation_setup = "automation-setup",
  flow = "flow",
  final = "final",
}

export interface WorkflowEditPageProps {
  workflow: ApiWorkflow;
  workflowVersion: ApiWorkflowVersion;
}

export const baseWorkflowSteps = [
  WorkflowEditPageStep.details,
  WorkflowEditPageStep.visibility,
  WorkflowEditPageStep.automation,
  WorkflowEditPageStep.flow,
];

const scheduledWorkflowSteps = [
  WorkflowEditPageStep.details,
  WorkflowEditPageStep.visibility,
  WorkflowEditPageStep.automation,
  WorkflowEditPageStep.automation_setup,
  WorkflowEditPageStep.flow,
];

export const getWorkflowSteps = (
  triggerType: ApiWorkflowTriggerType,
  triggerScheduleEntityType: ApiWorkflowTriggerScheduleEntityType | undefined
) => {
  if (hasAutomationConfigurationPage(triggerType, triggerScheduleEntityType)) {
    return scheduledWorkflowSteps;
  }

  return baseWorkflowSteps;
};

export const useWorkflowEditPage = () => {
  const { openModal } = useModals();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const confirmExit = () => {
    openModal(ConfirmationModal, {
      question: t("common:unsaved_changes_may_be_lost_title"),
      text: t("common:unsaved_changes_may_be_lost_description"),
      acceptTitle: t("common:leave"),
      secondButtonTitle: t("common:stay"),
      onAccept: async function () {
        navigate(`/workflows/library`);
      },
    });
  };

  const navigateToStep = (id: number, newStep: WorkflowEditPageStep) => {
    navigate(`/workflows/${id}/setup/${newStep}`);
  };

  return {
    confirmExit,
    navigateToStep,
  };
};

export interface ApiWorkflowEventTypeSelectItem extends SelectItem {
  id: ApiWorkflowEventTypes;
  event_types: [ApiWorkflowEventTypes, ApiWorkflowEventTypes?];
}

export const getApiWorkflowEventTypesSelectItems = (
  includeAtsEvents: boolean
): ApiWorkflowEventTypeSelectItem[] => {
  return [
    {
      id: ApiWorkflowEventTypes.employee_hired,
      event_types: [ApiWorkflowEventTypes.employee_hired, ApiWorkflowEventTypes.employee_created],
      title: i18n.t("workflow:event_type_employee_hired_when"),
      caption: i18n.t("workflow:event_type_employee_hired_when_example"),
    },
    {
      id: ApiWorkflowEventTypes.employee_terminated,
      event_types: [
        ApiWorkflowEventTypes.employee_terminated,
        ApiWorkflowEventTypes.employee_termination_created,
      ],
      title: i18n.t("workflow:event_type_employee_terminated_when"),
      caption: i18n.t("workflow:event_type_employee_terminated_when_example"),
    },
    {
      id: ApiWorkflowEventTypes.employee_position_changed,
      event_types: [
        ApiWorkflowEventTypes.employee_position_changed,
        ApiWorkflowEventTypes.employee_position_change_created,
      ],
      title: i18n.t("workflow:event_type_employee_position_changed_when"),
      caption: i18n.t("workflow:event_type_employee_position_changed_when_example"),
    },
    {
      id: ApiWorkflowEventTypes.employee_employment_changed,
      event_types: [
        ApiWorkflowEventTypes.employee_employment_changed,
        ApiWorkflowEventTypes.employee_employment_change_created,
      ],
      title: i18n.t("workflow:event_type_employee_employment_changed_when"),
      caption: i18n.t("workflow:event_type_employee_employment_changed_when_example"),
    },
    {
      id: ApiWorkflowEventTypes.employee_compensation_changed,
      event_types: [
        ApiWorkflowEventTypes.employee_compensation_changed,
        ApiWorkflowEventTypes.employee_compensation_change_created,
      ],
      title: i18n.t("workflow:event_type_employee_compensation_changed_when"),
      caption: i18n.t("workflow:event_type_employee_compensation_changed_when_example"),
    },
    {
      id: ApiWorkflowEventTypes.time_off_request_approved,
      event_types: [ApiWorkflowEventTypes.time_off_request_approved],
      title: i18n.t("workflow:event_type_time_off_request_approved_when"),
      caption: i18n.t("workflow:event_type_time_off_request_approved_when_example"),
    },
    ...(includeAtsEvents ? getApiWorkflowCandidateEventTypesSelectItems() : []),
  ];
};

const getApiWorkflowCandidateEventTypesSelectItems = (): ApiWorkflowEventTypeSelectItem[] => {
  return [
    {
      id: ApiWorkflowEventTypes.candidate_created,
      event_types: [ApiWorkflowEventTypes.candidate_created],
      title: i18n.t("workflow:event_type_candidate_created_when"),
      caption: i18n.t("workflow:event_type_candidate_created_when_example"),
    },
    {
      id: ApiWorkflowEventTypes.candidate_stage_changed,
      event_types: [ApiWorkflowEventTypes.candidate_stage_changed],
      title: i18n.t("workflow:event_type_candidate_stage_changed_when"),
      caption: i18n.t("workflow:event_type_candidate_stage_changed_when_example"),
    },
    {
      id: ApiWorkflowEventTypes.candidate_hired,
      event_types: [ApiWorkflowEventTypes.candidate_hired],
      title: i18n.t("workflow:event_type_candidate_hired_when"),
      caption: i18n.t("workflow:event_type_candidate_hired_when_example"),
    },
    {
      id: ApiWorkflowEventTypes.candidate_disqualified,
      event_types: [ApiWorkflowEventTypes.candidate_disqualified],
      title: i18n.t("workflow:event_type_candidate_disqualified_when"),
      caption: i18n.t("workflow:event_type_candidate_disqualified_when_example"),
    },
    {
      id: ApiWorkflowEventTypes.candidate_feedback_submitted,
      event_types: [ApiWorkflowEventTypes.candidate_feedback_submitted],
      title: i18n.t("workflow:event_type_candidate_feedback_submitted_when"),
      caption: i18n.t("workflow:event_type_candidate_feedback_submitted_when_example"),
    },
    {
      id: ApiWorkflowEventTypes.offer_approved,
      event_types: [ApiWorkflowEventTypes.offer_approved],
      title: i18n.t("workflow:event_type_offer_approved_when"),
      caption: i18n.t("workflow:event_type_offer_approved_when_example"),
    },
    {
      id: ApiWorkflowEventTypes.offer_rejected,
      event_types: [ApiWorkflowEventTypes.offer_rejected],
      title: i18n.t("workflow:event_type_offer_rejected_when"),
      caption: i18n.t("workflow:event_type_offer_rejected_when_example"),
    },
  ];
};

export interface ApiWorkflowScheduledEntityTypeUI {
  entity: ApiWorkflowTriggerScheduleEntityType;
  field: ApiWorkflowTriggerScheduleEntityField;
  title: string;
  caption: string;
  // default_field: ApiWorkflowTriggerScheduleEntityField;
}

export const getApiWorkflowScheduledEntityTypeSelectItems =
  (): ApiWorkflowScheduledEntityTypeUI[] => {
    return [
      {
        entity: ApiWorkflowTriggerScheduleEntityType.time_off,
        field: ApiWorkflowTriggerScheduleEntityField.date_start,
        title: i18n.t("workflow:trigger_schedule_entity_type_time_off"),
        caption: i18n.t("workflow:trigger_schedule_entity_type_time_off_example"),
      },
      {
        entity: ApiWorkflowTriggerScheduleEntityType.employee,
        field: ApiWorkflowTriggerScheduleEntityField.hire_date,
        title: i18n.t("workflow:trigger_schedule_entity_field_hire_date"),
        caption: i18n.t("workflow:trigger_schedule_entity_field_hire_date_example"),
      },
      {
        entity: ApiWorkflowTriggerScheduleEntityType.employee,
        field: ApiWorkflowTriggerScheduleEntityField.probation_ends_date,
        title: i18n.t("workflow:trigger_schedule_entity_field_probation_ends_date"),
        caption: i18n.t("workflow:trigger_schedule_entity_field_probation_ends_date_example"),
      },
      {
        entity: ApiWorkflowTriggerScheduleEntityType.employee,
        field: ApiWorkflowTriggerScheduleEntityField.terminate_date,
        title: i18n.t("workflow:trigger_schedule_entity_field_terminate_date"),
        caption: i18n.t("workflow:trigger_schedule_entity_field_terminate_date_example"),
      },
      {
        entity: ApiWorkflowTriggerScheduleEntityType.employee,
        field: ApiWorkflowTriggerScheduleEntityField.birthday,
        title: i18n.t("workflow:trigger_schedule_entity_field_birthday"),
        caption: i18n.t("workflow:trigger_schedule_entity_field_birthday_example"),
      },
      {
        entity: ApiWorkflowTriggerScheduleEntityType.employee,
        field: ApiWorkflowTriggerScheduleEntityField.work_anniversary,
        title: i18n.t("workflow:trigger_schedule_entity_field_work_anniversary"),
        caption: i18n.t("workflow:trigger_schedule_entity_field_work_anniversary_example"),
      },
      {
        entity: ApiWorkflowTriggerScheduleEntityType.employee,
        field: ApiWorkflowTriggerScheduleEntityField.custom_field,
        title: i18n.t("workflow:trigger_schedule_entity_field_custom_field"),
        caption: i18n.t("workflow:trigger_schedule_entity_field_custom_field_example"),
      },
      {
        entity: ApiWorkflowTriggerScheduleEntityType.custom_table_row,
        field: ApiWorkflowTriggerScheduleEntityField.custom_table_column,
        title: i18n.t("workflow:trigger_schedule_entity_field_custom_table_column"),
        caption: i18n.t("workflow:trigger_schedule_entity_field_custom_table_column_example"),
      },
    ];
  };

const customScheduleFieldEntity: Partial<
  Record<ApiWorkflowTriggerScheduleEntityField, EmployeeColumnEntity>
> = {
  [ApiWorkflowTriggerScheduleEntityField.custom_field]: EmployeeColumnEntity.custom_field,
  [ApiWorkflowTriggerScheduleEntityField.custom_table_column]: EmployeeColumnEntity.custom_table,
};

export const getCustomScheduleDateColumns = (
  field: ApiWorkflowTriggerScheduleEntityField,
  columns: ApiEmployeeColumn[] | undefined
): (ApiEmployeeColumn & { field_id: number })[] => {
  const entity = customScheduleFieldEntity[field];
  if (!entity || !columns) return [];
  return columns.filter(
    (column): column is ApiEmployeeColumn & { field_id: number } =>
      column.value_type === ApiEmployeeColumnValueType.date &&
      column.field_id !== null &&
      getEmployeeColumnEntity(column.id) === entity
  );
};

export const getApiWorkflowScheduledEntityType = (
  type: ApiWorkflowTriggerScheduleEntityType,
  field?: ApiWorkflowTriggerScheduleEntityField
) => {
  return getApiWorkflowScheduledEntityTypeSelectItems().find(
    (x) => x.entity == type && (field === undefined || x.field === field)
  );
};

export const hasAutomationConfigurationPage = (
  triggerType: ApiWorkflowTriggerType,
  triggerScheduleEntityType: ApiWorkflowTriggerScheduleEntityType | undefined
): boolean => {
  if (triggerType === ApiWorkflowTriggerType.recurring) {
    return true;
  }

  if (triggerType == ApiWorkflowTriggerType.scheduled) {
    if (triggerScheduleEntityType == ApiWorkflowTriggerScheduleEntityType.time_off) {
      return true;
    } else if (triggerScheduleEntityType == ApiWorkflowTriggerScheduleEntityType.employee) {
      return true;
    } else if (triggerScheduleEntityType == ApiWorkflowTriggerScheduleEntityType.custom_table_row) {
      return true;
    }
  }

  return false;
};
